import React from 'react';
import './Avatar.css';

/**
 * Reusable Avatar component
 *
 * @param {Object} props
 * @param {string} props.username - User's username (used for placeholder initial)
 * @param {string} props.avatarUrl - URL to user's profile image
 * @param {number} props.rank - User's rank (1=gold, 2=silver, 3=bronze outline)
 * @param {string} props.size - Size variant: 'xs', 'sm', 'md', 'lg', 'xl' (default: 'md')
 * @param {string} props.className - Additional CSS classes
 * @param {Object} props.style - Additional inline styles
 * @param {Function} props.onClick - Click handler
 */
function Avatar({
  username = 'U',
  avatarUrl,
  rank,
  size = 'md',
  className = '',
  style = {},
  onClick
}) {
  // Get the first character of username for placeholder
  const initial = username.charAt(0).toUpperCase();

  // Determine rank class for outline
  const getRankClass = () => {
    if (rank === 1) return 'avatar-rank-gold';
    if (rank === 2) return 'avatar-rank-silver';
    if (rank === 3) return 'avatar-rank-bronze';
    return '';
  };

  // Combine all classes
  const avatarClasses = [
    'avatar',
    `avatar-${size}`,
    getRankClass(),
    className
  ].filter(Boolean).join(' ');

  // Check if avatarUrl is valid (not empty string, null, or undefined)
  const hasValidAvatar = avatarUrl && avatarUrl.trim() !== '';

  // Fix legacy URLs that might have localhost hardcoded
  const getCleanAvatarUrl = (url) => {
    if (!url) return url;
    // Remove any hardcoded localhost:8000 prefix
    return url.replace(/^https?:\/\/localhost:8000/, '');
  };

  return (
    <div
      className={avatarClasses}
      style={style}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
    >
      {hasValidAvatar ? (
        <img src={getCleanAvatarUrl(avatarUrl)} alt={username} className="avatar-image" />
      ) : (
        <div className="avatar-placeholder">
          {initial}
        </div>
      )}
    </div>
  );
}

export default Avatar;
